/**
 * useCamera — Shared hook for initializing and managing a camera stream.
 * Encapsulates the getUserMedia setup, cleanup, and error handling logic.
 */

import { useRef, useState, useEffect } from "react";

interface UseCameraOptions {
  width?: number;
  height?: number;
}

export const useCamera = ({ width = 1920, height = 1080 }: UseCameraOptions = {}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const startCamera = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        if (isMounted) setError("Camera not supported in this browser");
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: width }, height: { ideal: height } },
          audio: false,
        });

        if (!isMounted) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
            if (isMounted) setIsReady(true);
          } catch (playErr: any) {
            if (playErr.name !== "AbortError" && isMounted) {
              setError(`Failed to start video: ${playErr.message}`);
            }
          }
        }
      } catch (e: any) {
        if (!isMounted) return;
        const name = e?.name || "";
        if (name === "NotAllowedError") setError("Camera access denied. Please allow camera access.");
        else if (name === "NotFoundError") setError("No camera found on this device.");
        else setError(`Camera error: ${e?.message || String(e)}`);
      }
    };

    startCamera();

    return () => {
      isMounted = false;
      setIsReady(false);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
    };
  }, [width, height]);

  /** Stop the camera stream manually (e.g. after single capture) */
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }
    setIsReady(false);
  };

  return { videoRef, isReady, error, setError, stopCamera };
};
